import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Tooltip,
} from "@mui/material";
import { format, isBefore, isValid, parse, startOfDay } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import QuestionIcon16pxSvg from "../../../assets/images/icons/QuestionIcon16pxSvg";
import DefaultTooltip, { LeaveUsedDaysTooltipContent } from "../../../components/DefaultTooltip";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";
import { getLeaveByUser, getLeaveByUserByLeaveId } from "../../../axios/User/UserApi";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog } from "../../../components/StyledComponents";
import { useLoading } from "../../../context/LoadingContext";
import { useAuth } from "../../../context/AuthContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CustomDateTimeRangePicker from "../../../components/CustomDateTimeRangePicker";
dayjs.extend(utc);
dayjs.extend(timezone);

let periodIdCounter = 0;
const createPeriodId = () => `period-${++periodIdCounter}`;
const DEFAULT_FULL_DAY_MINUTES = 8 * 60;

const parseTimeStringToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(":").map(Number);
  const [h = 0, m = 0, s = 0] = parts;
  return h * 60 + m + (Number.isFinite(s) ? s / 60 : 0);
};

/** Chuyển chuỗi thời gian HH:mm:ss sang tổng giây */
const parseTimeStringToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(":").map(Number);
  const [h = 0, m = 0, s = 0] = parts;
  return (
    (Number.isFinite(h) ? h : 0) * 3600 +
    (Number.isFinite(m) ? m : 0) * 60 +
    (Number.isFinite(s) ? s : 0)
  );
};

/** Chuẩn hóa hoursPerLeaveDay về "HH:mm:ss" */
const normalizeHoursPerLeaveDay = (value) => {
  if (!value) return "08:00:00";
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || "08:00:00";
  }
  if (typeof value === "object") {
    const hour = String(value.hour ?? 0).padStart(2, "0");
    const minute = String(value.minute ?? 0).padStart(2, "0");
    const second = String(value.second ?? 0).padStart(2, "0");
    return `${hour}:${minute}:${second}`;
  }
  return "08:00:00";
};

/** Phút → "HH:mm:ss" (giờ có thể > 24) */
/** Phút (có thể lẻ giây) → "HH:mm:ss" (giữ giây) */
const minutesToHhMmSs = (totalMinutes) => {
  const totalSeconds = Math.max(
    0,
    Math.round((Number(totalMinutes) || 0) * 60)
  );
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/** Số ngày (theo leaveType) → "HH:mm:ss" */
const daysToHhMmSs = (days, hoursPerLeaveDayMinutes) => {
  const dayMins =
    hoursPerLeaveDayMinutes > 0
      ? hoursPerLeaveDayMinutes
      : DEFAULT_FULL_DAY_MINUTES;
  return minutesToHhMmSs(Number(days || 0) * dayMins);
};

const parseHoursOneDayToMinutes = (value) => {
  if (!value) return 8 * 60;
  if (String(value).includes(":")) {
    return parseTimeStringToMinutes(value);
  }
  return Number(value) * 60 || 8 * 60;
};

const formatHoursOneDayLabel = (value) => {
  if (!value) return "8";
  if (String(value).includes(":")) {
    const totalMinutes = parseTimeStringToMinutes(value);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (minutes === 0) return String(hours);
    return `${hours}:${String(minutes).padStart(2, "0")}`;
  }
  return String(value);
};

/** Chuyển tổng phút sang { days, hours, minutes, seconds } theo số giờ/ngày cấu hình */
const minutesToDurationParts = (totalMinutes, minutesPerDay) => {
  const dayMins =
    Number(minutesPerDay) > 0 ? Number(minutesPerDay) : DEFAULT_FULL_DAY_MINUTES;
  const totalSeconds = Math.max(0, Math.round((Number(totalMinutes) || 0) * 60));
  const daySeconds = Math.round(dayMins * 60);
  const days = Math.floor(totalSeconds / daySeconds);
  let rem = totalSeconds % daySeconds;
  const hours = Math.floor(rem / 3600);
  rem %= 3600;
  const minutes = Math.floor(rem / 60);
  const seconds = rem % 60;
  return { days, hours, minutes, seconds };
};

/** Format duration hiển thị trên UI (có giây) */
const formatDurationLabel = (parts, t) => {
  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = parts || {};
  const hasHms = hours > 0 || minutes > 0 || seconds > 0;

  if (days > 0 && hasHms) {
    return t("leave-request.duration-days-hours-mins-secs", {
      days,
      hours,
      mins: minutes,
      secs: seconds,
    });
  }
  if (days > 0) {
    return t("leave-request.duration-days", { days });
  }
  if (hours > 0) {
    return t("leave-request.duration-hours-mins-secs", {
      hours,
      mins: minutes,
      secs: seconds,
    });
  }
  if (minutes > 0) {
    return t("leave-request.duration-mins-secs", {
      mins: minutes,
      secs: seconds,
    });
  }
  if (seconds > 0) {
    return t("leave-request.duration-secs", { secs: seconds });
  }
  return t("leave-request.duration-mins", { mins: 0 });
};

/** Lấy tên hiển thị loại phép theo ngôn ngữ hiện tại */
const getLeaveTypeDisplayName = (leaveTypesData, leaveTypeId) => {
  const option = leaveTypesData?.find(
    (item) => String(item.leaveTypeId) === String(leaveTypeId)
  );
  if (!option) return "—";
  const langId = Number(localStorage.getItem("i18nextLngId")) || 1;
  if (langId === 1) return option.remark || option.leaveTypeName || "—";
  if (langId === 2) return option.remarkEN || option.leaveTypeName || "—";
  return option.leaveTypeName || "—";
};

const getOverlapMinutes = (startA, endA, startB, endB) => {
  const overlapStart = Math.max(startA.getTime(), startB.getTime());
  const overlapEnd = Math.min(endA.getTime(), endB.getTime());
  if (overlapEnd <= overlapStart) return 0;
  return (overlapEnd - overlapStart) / (1000 * 60);
};

const getBreakOverlapMinutes = (rangeStart, rangeEnd, breakTimes, dayDate, combineDateAndTime) => {
  if (!breakTimes?.length) return 0;

  let breakMinutes = 0;
  for (const breakTime of breakTimes) {
    if (!breakTime?.breakStart || !breakTime?.durationHours) continue;

    const breakStart = combineDateAndTime(dayDate, breakTime.breakStart);
    const durationMin = parseTimeStringToMinutes(breakTime.durationHours);
    const breakEnd = dayjs(breakStart).add(durationMin, "minute").toDate();

    breakMinutes += getOverlapMinutes(rangeStart, rangeEnd, breakStart, breakEnd);
  }
  return breakMinutes;
};

/**
 * Tìm thời điểm sau khi cộng `targetWorkMinutes` phút làm việc
 * (bỏ qua khoảng nghỉ giữa ca) trong [rangeStart, rangeEnd].
 */
const findTimeAfterWorkMinutes = (
  rangeStart,
  rangeEnd,
  targetWorkMinutes,
  breakTimes,
  dayDate,
  combineDateAndTime
) => {
  if (!rangeStart || !rangeEnd) return rangeStart;
  if (targetWorkMinutes <= 0) return rangeStart;

  const breaks = [];
  if (breakTimes?.length) {
    for (const breakTime of breakTimes) {
      if (!breakTime?.breakStart || !breakTime?.durationHours) continue;
      const breakStart = combineDateAndTime(dayDate, breakTime.breakStart);
      const durationMin = parseTimeStringToMinutes(breakTime.durationHours);
      const breakEnd = dayjs(breakStart).add(durationMin, "minute").toDate();
      breaks.push({ start: breakStart, end: breakEnd });
    }
    breaks.sort((a, b) => a.start - b.start);
  }

  let cursor = dayjs(rangeStart);
  const end = dayjs(rangeEnd);
  let remaining = targetWorkMinutes;

  while (cursor.isBefore(end) && remaining > 0) {
    const coveringBreak = breaks.find(
      (b) => !cursor.isBefore(dayjs(b.start)) && cursor.isBefore(dayjs(b.end))
    );
    if (coveringBreak) {
      cursor = dayjs(coveringBreak.end);
      continue;
    }

    const nextBreak = breaks.find((b) => dayjs(b.start).isAfter(cursor));
    const segmentEnd =
      nextBreak && dayjs(nextBreak.start).isBefore(end)
        ? dayjs(nextBreak.start)
        : end;

    const segmentMinutes = segmentEnd.diff(cursor, "minute", true);
    if (segmentMinutes <= remaining + 1e-9) {
      remaining -= segmentMinutes;
      cursor = segmentEnd;
      if (nextBreak && remaining > 0) {
        cursor = dayjs(nextBreak.end);
      }
    } else {
      cursor = cursor.add(remaining, "minute");
      remaining = 0;
    }
  }

  return cursor.toDate();
};

/** Làm tròn số ngày phép (2 chữ số thập phân) */
const roundNumOfDay = (value) => Math.round(Number(value || 0) * 100) / 100;

/** Hiển thị số thập phân tối đa 2 chữ số, số nguyên giữ nguyên */
const formatDecimalNumber = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return value ?? 0;
  if (Number.isInteger(numberValue)) return numberValue;

  return Number(numberValue.toFixed(2));
};

const formatOneDecimal = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return 0;
  const rounded = Math.round(numberValue * 100) / 100;
  return Number.isInteger(rounded) ? rounded : rounded;
};

const parseUsedFromYearToDays = (usedFromYear, hoursPerLeaveDay) => {
  if (usedFromYear == null || usedFromYear === "") return 0;

  const asString = String(usedFromYear).trim();
  if (asString.includes(":")) {
    const timeMins = parseTimeStringToMinutes(asString);
    const dayMins =
      parseTimeStringToMinutes(hoursPerLeaveDay) || DEFAULT_FULL_DAY_MINUTES;
    if (dayMins <= 0) return 0;
    return timeMins / dayMins;
  }

  const asNumber = Number(usedFromYear);
  return Number.isFinite(asNumber) ? asNumber : 0;
};

const formatLeaveBalanceDisplay = (value, hoursPerLeaveDay) =>
  formatDecimalNumber(parseUsedFromYearToDays(value, hoursPerLeaveDay));

const resolveItemHoursPerLeaveDay = (item) =>
  normalizeHoursPerLeaveDay(
    item?.hoursPerLeaveDaySnapshot || item?.hoursPerLeaveDay || "08:00:00"
  );

/** Phần còn lại: totalEntitlement − usedFromYear, tính theo giây để không mất giây lẻ */
const formatLeaveEntitlementAvailabilityText = (items, t) => {
  const list = toLeaveBalanceItems(items);
  if (!list.length) {
    return t("common.leave-entitlement-exhausted");
  }

  const hoursPerDay = resolveItemHoursPerLeaveDay(list[0]);
  const daySeconds =
    parseTimeStringToSeconds(hoursPerDay) || DEFAULT_FULL_DAY_MINUTES * 60;
  const totalDays = Number(list[0].totalEntitlementYear) || 0;
  const totalSeconds = Math.round(totalDays * daySeconds);

  const usedSeconds = list.reduce((sum, item) => {
    const usedRaw = item.usedFromYear;
    if (usedRaw == null || usedRaw === "") return sum;

    const asString = String(usedRaw).trim();
    if (asString.includes(":")) {
      return sum + parseTimeStringToSeconds(asString);
    }

    const days = Number(usedRaw);
    if (!Number.isFinite(days)) return sum;
    const itemDaySecs =
      parseTimeStringToSeconds(resolveItemHoursPerLeaveDay(item)) ||
      DEFAULT_FULL_DAY_MINUTES * 60;
    return sum + Math.round(days * itemDaySecs);
  }, 0);

  const remainingSeconds = totalSeconds - usedSeconds;
  if (!(remainingSeconds > 0)) {
    return t("common.leave-entitlement-exhausted");
  }

  const days = Math.floor(remainingSeconds / daySeconds);
  let rem = remainingSeconds % daySeconds;
  const hours = Math.floor(rem / 3600);
  rem %= 3600;
  const minutes = Math.floor(rem / 60);
  const seconds = rem % 60;

  return t("common.leave-entitlement-remaining", {
    days,
    hours,
    minutes,
    seconds,
  });
};

const toLeaveBalanceItems = (leaveBalance) => {
  if (!leaveBalance) return [];
  return Array.isArray(leaveBalance) ? leaveBalance.filter(Boolean) : [leaveBalance];
};

const sumUsedFromYearDays = (items) =>
  toLeaveBalanceItems(items).reduce(
    (sum, item) =>
      sum +
      parseUsedFromYearToDays(item.usedFromYear, resolveItemHoursPerLeaveDay(item)),
    0
  );

const sumUsedFromCarryDays = (items) =>
  toLeaveBalanceItems(items).reduce(
    (sum, item) =>
      sum +
      parseUsedFromYearToDays(item.usedFromCarry, resolveItemHoursPerLeaveDay(item)),
    0
  );

const buildUsedFromYearDetails = (items) =>
  toLeaveBalanceItems(items).map((item) => {
    const hoursPerDay = resolveItemHoursPerLeaveDay(item);
    const usedRaw = item.usedFromYear;
    const usedTime =
      typeof usedRaw === "string" && String(usedRaw).includes(":")
        ? normalizeHoursPerLeaveDay(usedRaw)
        : minutesToHhMmSs(
          parseUsedFromYearToDays(usedRaw, hoursPerDay) *
          (parseTimeStringToMinutes(hoursPerDay) || DEFAULT_FULL_DAY_MINUTES)
        );
    return {
      usedTime,
      hoursPerDay,
      days: formatOneDecimal(parseUsedFromYearToDays(usedRaw, hoursPerDay)),
    };
  });

const normalizeLeaveBalanceData = (data) => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

/** Phút làm việc đầy đủ 1 ngày theo ca (đã trừ break) */
const getFullShiftDayMinutes = (shift, combineDateAndTime, refDate, fallbackMinutes) => {
  if (!shift) return fallbackMinutes || 8 * 60;

  const shiftStart = shift.shiftStart || "00:00:00";
  const shiftEnd = shift.shiftEnd || "23:59:00";
  let dayStart = combineDateAndTime(refDate, shiftStart);
  let dayEnd = combineDateAndTime(refDate, shiftEnd);

  if (!dayStart || !dayEnd) return fallbackMinutes || 8 * 60;

  if (dayjs(dayEnd).isBefore(dayjs(dayStart))) {
    dayEnd = dayjs(dayEnd).add(1, "day").toDate();
  }

  let minutes = (dayEnd.getTime() - dayStart.getTime()) / (1000 * 60);

  if (shift?.breakTimes?.length) {
    minutes -= getBreakOverlapMinutes(
      dayStart,
      dayEnd,
      shift.breakTimes,
      refDate,
      combineDateAndTime
    );
  }

  const result = Math.max(0, Math.round(minutes));
  return result > 0 ? result : fallbackMinutes || 8 * 60;
};

/**
 * Tách period thành từng ngày kèm dayUnits theo hoursPerLeaveDay của leaveType
 * (1 ngày phép = hoursPerLeaveDay phút, tối đa 1 ngày/ngày lịch)
 */
const getPeriodDayBreakdown = (start, end, shift, combineDateAndTime, hoursPerLeaveDayMinutes) => {
  if (!start || !end) return [];

  const startDt = dayjs(start);
  const endDt = dayjs(end);
  if (!startDt.isValid() || !endDt.isValid() || endDt.isBefore(startDt)) return [];

  const shiftStart = shift?.shiftStart || "00:00:00";
  const shiftEnd = shift?.shiftEnd || "23:59:00";
  const dayUnitMinutes =
    hoursPerLeaveDayMinutes > 0
      ? hoursPerLeaveDayMinutes
      : DEFAULT_FULL_DAY_MINUTES;
  const days = [];

  let cursor = startDt.startOf("day");
  const lastDay = endDt.startOf("day");

  while (cursor.isBefore(lastDay) || cursor.isSame(lastDay, "day")) {
    const dayDate = cursor.toDate();
    const dayStart = combineDateAndTime(dayDate, shiftStart);
    let dayEnd = combineDateAndTime(dayDate, shiftEnd);

    if (dayjs(dayEnd).isBefore(dayjs(dayStart))) {
      dayEnd = dayjs(dayEnd).add(1, "day").toDate();
    }

    // range* / leave* = giao giữa giờ user nhập và khung ca (hỗ trợ ca qua đêm)
    const rangeStart = startDt.isAfter(dayjs(dayStart)) ? startDt.toDate() : dayStart;
    const rangeEnd = endDt.isBefore(dayjs(dayEnd)) ? endDt.toDate() : dayEnd;
    const leaveStart = rangeStart;
    const leaveEnd = rangeEnd;

    if (rangeEnd > rangeStart) {
      let dayMinutes = (rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60);

      if (shift?.breakTimes?.length) {
        dayMinutes -= getBreakOverlapMinutes(
          rangeStart,
          rangeEnd,
          shift.breakTimes,
          dayDate,
          combineDateAndTime
        );
      }

      // Giữ độ chính xác giây; so sánh theo giây để không làm tròn lên đủ 1 ngày
      dayMinutes = Math.max(0, dayMinutes);
      const daySeconds = Math.round(dayMinutes * 60);
      const dayUnitSeconds = Math.round(dayUnitMinutes * 60);
      // Cap 1 ngày phép / ngày lịch (vd ca 00:00–23:59 không thành 3 ngày khi hoursPerLeaveDay=8h)
      const cappedSeconds = Math.min(daySeconds, dayUnitSeconds);
      const cappedMinutes = cappedSeconds / 60;
      // Chỉ đủ 1 ngày khi giây >= hoursPerLeaveDay; dưới ngưỡng không round lên 1.00
      const dayUnits =
        cappedSeconds >= dayUnitSeconds
          ? 1
          : Math.min(0.99, roundNumOfDay(cappedMinutes / dayUnitMinutes));

      if (cappedSeconds > 0) {
        days.push({
          date: cursor.format("YYYY-MM-DD"),
          year: cursor.year(),
          minutes: cappedMinutes,
          dayUnits,
          rangeStart,
          rangeEnd,
          leaveStart,
          leaveEnd,
        });
      }
    }

    cursor = cursor.add(1, "day");
  }

  return days;
};

/** Chia ngày paid / unpaid theo số ngày phép còn lại (ưu tiên theo thứ tự thời gian).
 * Nếu còn phép < 1 ngày trong một ngày ca → cắt giữa ngày (paid rồi unpaid), không đẩy cả ngày sang unpaid.
 */
const splitDaysByPaidBalance = (
  dayBreakdown,
  availablePaidDays,
  shift,
  combineDateAndTime,
  fallbackMinutes
) => {
  let remaining = Number(availablePaidDays);
  if (!Number.isFinite(remaining)) {
    return { paidDays: [...dayBreakdown], unpaidDays: [] };
  }

  const paidDays = [];
  const unpaidDays = [];

  for (const day of dayBreakdown) {
    if (remaining <= 1e-9) {
      unpaidDays.push(day);
      continue;
    }

    if (day.dayUnits <= remaining + 1e-9) {
      paidDays.push(day);
      remaining = roundNumOfDay(remaining - day.dayUnits);
      continue;
    }

    const fullDayMinutes =
      day.dayUnits > 0
        ? Math.round(day.minutes / day.dayUnits)
        : fallbackMinutes || DEFAULT_FULL_DAY_MINUTES;

    const paidMinutesNeeded = Math.max(
      0,
      Math.min(day.minutes, Math.round(remaining * fullDayMinutes))
    );

    if (paidMinutesNeeded <= 0) {
      unpaidDays.push(day);
      remaining = 0;
      continue;
    }

    if (paidMinutesNeeded >= day.minutes) {
      paidDays.push(day);
      remaining = 0;
      continue;
    }

    const dayDate = dayjs(day.date).toDate();
    const splitAt = findTimeAfterWorkMinutes(
      day.leaveStart,
      day.leaveEnd,
      paidMinutesNeeded,
      shift?.breakTimes,
      dayDate,
      combineDateAndTime
    );

    const unpaidMinutes = day.minutes - paidMinutesNeeded;

    paidDays.push({
      ...day,
      leaveEnd: splitAt,
      rangeEnd: splitAt,
      minutes: paidMinutesNeeded,
      dayUnits: roundNumOfDay(paidMinutesNeeded / fullDayMinutes),
    });

    unpaidDays.push({
      ...day,
      leaveStart: splitAt,
      rangeStart: splitAt,
      minutes: unpaidMinutes,
      dayUnits: roundNumOfDay(unpaidMinutes / fullDayMinutes),
    });

    remaining = 0;
  }

  return { paidDays, unpaidDays };
};

/** Tính số ngày phép còn lại + carry từ entitlement (gộp mọi bản ghi cùng loại) */
const getRemainingBalanceFromEntitlement = (leaveBalance, usedAdjustment = 0) => {
  const items = toLeaveBalanceItems(leaveBalance);
  if (!items.length) {
    return { remainingPaidDays: 0, carryAvailable: 0 };
  }

  const first = items[0];
  const usedFromYear = Math.max(
    sumUsedFromYearDays(items) - (Number(usedAdjustment) || 0),
    0
  );
  const totalEntitlementYear = Number(first.totalEntitlementYear) || 0;

  let carryAvailable = 0;
  if (first?.isCarry) {
    let expiredOk = true;
    if (first?.expiredDt) {
      const now = new Date();
      const expiredDate = new Date(first.expiredDt);
      now.setHours(0, 0, 0, 0);
      expiredDate.setHours(0, 0, 0, 0);
      expiredOk = now <= expiredDate;
    }
    if (expiredOk) {
      carryAvailable = Math.max(
        (Number(first.carryIn) || 0) - sumUsedFromCarryDays(items),
        0
      );
    }
  }

  return {
    remainingPaidDays:
      Math.max(totalEntitlementYear - usedFromYear, 0) + carryAvailable,
    carryAvailable,
  };
};

/**
 * Phần vượt phép: ưu tiên trừ leaveToBeDeducted nếu còn phép,
 * phần còn lại mới ghi nhận unpaid (leaveTypeId = 1)
 */
const splitExcessByDeductLeave = (
  excessDays,
  deductRemainingDays,
  shift,
  combineDateAndTime,
  fallbackMinutes
) => {
  if (!excessDays?.length) {
    return { bridgeDays: [], unpaidDays: [] };
  }
  if (!(Number(deductRemainingDays) > 0)) {
    return { bridgeDays: [], unpaidDays: [...excessDays] };
  }
  const { paidDays: bridgeDays, unpaidDays } = splitDaysByPaidBalance(
    excessDays,
    deductRemainingDays,
    shift,
    combineDateAndTime,
    fallbackMinutes
  );
  return { bridgeDays, unpaidDays };
};

/**
 * Tính:
 * - dayUnits: dùng cho entitlement / balance
 * - minutes: dùng để tạo numOfTime*
 *
 * QUAN TRỌNG:
 * Không được lấy dayUnits đã round để nhân ngược lại thành phút,
 * vì sẽ gây sai số:
 *
 * 690 phút / 480 = 1.4375
 * round -> 1.44
 * 1.44 * 480 = 691.2
 * round -> 691 phút = 11:31
 *
 * Vì vậy numOfTime* phải lấy trực tiếp từ minutes.
 */
const buildDayCountFields = (
  days,
  carryAvailable,
  hoursPerLeaveDayMinutes
) => {
  if (!days?.length) {
    return {
      dayUnits: 0,
      dayUnitsStart: 0,
      dayUnitsEnd: 0,

      dayUnitsCarryStart: 0,
      dayUnitsCarryEnd: 0,

      carryUsed: 0,

      // Minutes thực tế
      totalMinutes: 0,
      startYearMinutes: 0,
      endYearMinutes: 0,

      // Minutes thực tế dùng cho carry
      carryStartMinutes: 0,
      carryEndMinutes: 0,
    };
  }

  const dayMinutes =
    Number(hoursPerLeaveDayMinutes) > 0
      ? Number(hoursPerLeaveDayMinutes)
      : DEFAULT_FULL_DAY_MINUTES;

  const startYear = days[0].year;
  const endYear = days[days.length - 1].year;

  /*
   * ============================================================
   * 1. TÍNH MINUTES THỰC TẾ
   * ============================================================
   */

  const totalMinutes = days.reduce(
    (sum, day) => sum + (Number(day.minutes) || 0),
    0
  );

  const startYearMinutes = days
    .filter((day) => day.year === startYear)
    .reduce((sum, day) => sum + (Number(day.minutes) || 0), 0);

  const endYearMinutes =
    startYear === endYear
      ? 0
      : days
        .filter((day) => day.year === endYear)
        .reduce((sum, day) => sum + (Number(day.minutes) || 0), 0);

  /*
   * ============================================================
   * 2. TÍNH DAY UNITS
   *
   * Phần này vẫn round vì dayUnits phục vụ balance/entitlement.
   * KHÔNG dùng dayUnits để tạo numOfTime.
   * ============================================================
   */

  let dayUnitsStart = 0;
  let dayUnitsEnd = 0;

  if (startYear === endYear) {
    dayUnitsStart = roundNumOfDay(
      days.reduce(
        (sum, day) => sum + (Number(day.dayUnits) || 0),
        0
      )
    );

    dayUnitsEnd = 0;
  } else {
    days.forEach((day) => {
      if (day.year === startYear) {
        dayUnitsStart += Number(day.dayUnits) || 0;
      } else if (day.year === endYear) {
        dayUnitsEnd += Number(day.dayUnits) || 0;
      }
    });

    dayUnitsStart = roundNumOfDay(dayUnitsStart);
    dayUnitsEnd = roundNumOfDay(dayUnitsEnd);
  }

  const dayUnits = roundNumOfDay(dayUnitsStart + dayUnitsEnd);

  /*
   * ============================================================
   * 3. TÍNH CARRY
   *
   * carryAvailable đang là số ngày.
   * Chuyển carryAvailable -> minutes một lần duy nhất.
   *
   * Ưu tiên End trước rồi mới Start như logic cũ.
   * ============================================================
   */

  let carryLeftMinutes =
    Math.max(0, Number(carryAvailable) || 0) * dayMinutes;

  /*
   * Carry của END
   */
  const carryEndMinutes = Math.min(
    carryLeftMinutes,
    Math.max(0, endYearMinutes)
  );

  carryLeftMinutes -= carryEndMinutes;

  /*
   * Carry của START
   */
  const carryStartMinutes = Math.min(
    carryLeftMinutes,
    Math.max(0, startYearMinutes)
  );

  carryLeftMinutes -= carryStartMinutes;

  /*
   * Chuyển carry minutes -> day units chỉ để phục vụ balance.
   *
   * Không dùng giá trị này để tạo numOfTimeCarry*.
   */
  const dayUnitsCarryEnd = roundNumOfDay(
    carryEndMinutes / dayMinutes
  );

  const dayUnitsCarryStart = roundNumOfDay(
    carryStartMinutes / dayMinutes
  );

  const carryUsed = roundNumOfDay(
    dayUnitsCarryStart + dayUnitsCarryEnd
  );

  return {
    /*
     * Day units
     */
    dayUnits,
    dayUnitsStart,
    dayUnitsEnd,

    dayUnitsCarryStart,
    dayUnitsCarryEnd,

    carryUsed,

    /*
     * Minutes thực tế
     */
    totalMinutes,
    startYearMinutes,
    endYearMinutes,

    /*
     * Minutes carry thực tế
     */
    carryStartMinutes,
    carryEndMinutes,
  };
};

/**
 * Tạo newData từ danh sách ngày đã tách (paid/unpaid)
 *
 * QUAN TRỌNG:
 * numOfTime / numOfTimeStart / numOfTimeEnd
 * phải được tính trực tiếp từ minutes thực tế.
 *
 * Không được:
 *
 * minutes
 *   -> dayUnits
 *   -> round
 *   -> nhân lại hoursPerLeaveDay
 *
 * vì sẽ gây sai số phút.
 */
const buildLeaveNewDataFromDays = ({
  days,
  userId,
  shiftId,
  leaveTypeId,
  requestReason,
  carryAvailable,
  toUTC,
  leaveId,
  hoursPerLeaveDaySnapshot,
}) => {
  if (!days?.length) {
    return null;
  }

  /*
   * ============================================================
   * 1. Hours per leave day
   * ============================================================
   */

  const hoursPerLeaveDayMinutes =
    parseTimeStringToMinutes(hoursPerLeaveDaySnapshot) ||
    DEFAULT_FULL_DAY_MINUTES;

  /*
   * ============================================================
   * 2. Remap dayUnits theo leaveType hiện tại
   *
   * dayUnits vẫn được round vì dùng cho balance.
   *
   * Nhưng numOfTime KHÔNG lấy từ dayUnits này.
   * ============================================================
   */

  const remappedDays = days.map((day) => ({
    ...day,

    dayUnits: roundNumOfDay(
      (Number(day.minutes) || 0) /
      hoursPerLeaveDayMinutes
    ),
  }));

  /*
   * ============================================================
   * 3. Start / End thực tế
   * ============================================================
   */

  const start =
    remappedDays[0].leaveStart ??
    remappedDays[0].rangeStart;

  const end =
    remappedDays[remappedDays.length - 1].leaveEnd ??
    remappedDays[remappedDays.length - 1].rangeEnd;

  const startIso = toUTC(start);
  const endIso = toUTC(end);

  /*
   * ============================================================
   * 4. Tính counts
   *
   * buildDayCountFields sẽ trả cả:
   *
   * - dayUnits
   * - startYearMinutes
   * - endYearMinutes
   * - carryStartMinutes
   * - carryEndMinutes
   * ============================================================
   */

  const counts = buildDayCountFields(
    remappedDays,
    carryAvailable,
    hoursPerLeaveDayMinutes
  );

  /*
   * ============================================================
   * 5. Tạo newData
   *
   * numOfTime* lấy trực tiếp từ minutes.
   *
   * Đây là điểm fix lỗi 11:31.
   * ============================================================
   */

  const newData = {
    ...(leaveId != null
      ? {
        leaveId: Number(leaveId),
      }
      : {}),

    userId: Number(userId),

    shiftId:
      shiftId != null
        ? Number(shiftId)
        : null,

    start: startIso,

    end: endIso,

    leaveTypeId: Number(leaveTypeId),

    leavePolicyId: null,

    /*
     * ========================================================
     * TOTAL
     *
     * Ví dụ:
     * 690 minutes
     * => 11:30:00
     *
     * Không còn:
     * 1.44 * 480 = 691.2
     * ========================================================
     */
    numOfTime: minutesToHhMmSs(
      counts.totalMinutes
    ),

    /*
     * ========================================================
     * START YEAR
     * ========================================================
     */
    numOfTimeStart: minutesToHhMmSs(
      counts.startYearMinutes
    ),

    /*
     * ========================================================
     * CARRY START
     * ========================================================
     */
    numOfTimeCarryStart: minutesToHhMmSs(
      counts.carryStartMinutes
    ),

    /*
     * ========================================================
     * END YEAR
     * ========================================================
     */
    numOfTimeEnd: minutesToHhMmSs(
      counts.endYearMinutes
    ),

    /*
     * ========================================================
     * CARRY END
     * ========================================================
     */
    numOfTimeCarryEnd: minutesToHhMmSs(
      counts.carryEndMinutes
    ),

    hoursPerLeaveDaySnapshot:
      normalizeHoursPerLeaveDay(
        hoursPerLeaveDaySnapshot
      ),

    requestReason:
      requestReason || "",

    responseReason: "",
  };

  return {
    startIso,
    endIso,

    counts,

    newData,

    /*
     * Các giá trị này vẫn trả về theo dayUnits
     * để code phía dưới đang sử dụng không bị ảnh hưởng.
     */
    _carryUsed: counts.carryUsed,

    _dayUnits: counts.dayUnits,
  };
};

const calculateLeaveAllocation = ({
  totalMinutes,
  minutesPerDay,
  usedDays,
  totalEntitlement,
  lateEarlyMinutes,
}) => {
  const fullDaysFromRequest = Math.floor(totalMinutes / minutesPerDay);
  const remainderMinutes = totalMinutes % minutesPerDay;

  const availableDays = Math.max(totalEntitlement - usedDays, 0);

  const paidDays = Math.min(fullDaysFromRequest, availableDays);
  const unpaidFromDays = fullDaysFromRequest - paidDays;
  let remainingBalance = availableDays - paidDays;

  const totalExtraMinutes = remainderMinutes + lateEarlyMinutes;
  let convertedDays = 0;
  let timeLeftMinutes = totalExtraMinutes;

  if (totalExtraMinutes >= minutesPerDay) {
    convertedDays = Math.floor(totalExtraMinutes / minutesPerDay);
    timeLeftMinutes = totalExtraMinutes % minutesPerDay;
  }

  const paidFromConversion = Math.min(convertedDays, remainingBalance);
  const unpaidFromConversion = convertedDays - paidFromConversion;
  remainingBalance -= paidFromConversion;

  return {
    requested: minutesToDurationParts(totalMinutes, minutesPerDay),
    paid: {
      days: paidDays + paidFromConversion,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },
    unpaid: {
      days: unpaidFromDays + unpaidFromConversion,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },
    timeLeft: minutesToDurationParts(timeLeftMinutes, minutesPerDay),
    availableDays,
    hasExcess: unpaidFromDays + unpaidFromConversion > 0,
  };
};

const toDateForPicker = (value, dateTimeFormat) => {
  if (!value) return null;

  if (value instanceof Date && isValid(value)) {
    return value;
  }

  if (typeof value === "string") {
    const clean = value.replace(/\u00A0/g, " ").trim();

    if (/^\d{4}-\d{2}-\d{2}T/.test(clean)) {
      const isoDate = new Date(clean);
      return isValid(isoDate) ? isoDate : null;
    }

    const parsed = parse(clean, dateTimeFormat, new Date());
    if (isValid(parsed)) return parsed;
  }

  return null;
};

const EditLeaveDialog = ({
  onClose,
  onRefresh,
  leaveInfo,
  leaveTypesData,
  shiftsData,
  selectedDate,
  open,
  isLeaveMode = false,
}) => {
  const { t } = useTranslation();
  const {
    dateTimeFormat,
    isAdmin,
    genaralSettingDateFormat,
    genaralSettingTimeZone,
    leaveToBeDeducted,
  } = useAuth();
  const { editLeaveRequest, addLeaveRequest, getLeaveById, getLeaveRequestId } = LeaveApi();
  const [show, hide] = useLoading();

  const MAX_REASON_LENGTH = 255;

  const sortedShifts = useMemo(
    () => [...shiftsData].sort((a, b) => b.shiftId - a.shiftId),
    [shiftsData]
  );

  const [leaveByUser, setLeaveByUserData] = useState([]);
  const [leaveByUserByLeave, setLeaveByUserByLeaveData] = useState([]);
  const [leavePeriods, setLeavePeriods] = useState([]);
  const [datepickerKey, setDatepickerKey] = useState();
  const [isInputValid, setIsInputValid] = useState(false);
  const [oldLeaveData, setOldLeaveData] = useState(null);
  const [loadingLeaveEntitlement, setLoadingLeaveEntitlement] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const lateEarlyUsedTime =
    leaveByUser.find((item) => item.leaveTypeId === 1)?.usedFromYear || "00:00:00";

  const [formData, setFormData] = useState({
    userId: leaveInfo.userId,
    selectUser: leaveInfo.name,
    leaveType:
      leaveInfo.leaveTypeId != null && leaveInfo.leaveTypeId !== ""
        ? Number(leaveInfo.leaveTypeId)
        : "",
    requestReason: leaveInfo.requestReason,
    approveReason: leaveInfo.responseReason,
    approvedFrom: toDateForPicker(leaveInfo.requestStartDate, dateTimeFormat),
    approvedTo: toDateForPicker(leaveInfo.requestEndDate, dateTimeFormat),
    approvalStatusId: leaveInfo.approvalStatusId,
  });

  // Không cho chọn leaveTypeId = 1 (AR / hourly) trên dropdown
  const selectableLeaveTypes = useMemo(
    () =>
      (leaveTypesData || [])
        .map((item) => ({
          ...item,
          leaveTypeId: Number(item.leaveTypeId),
        }))
        .filter((item) => item.leaveTypeId !== 1),
    [leaveTypesData]
  );

  // Nếu leaveType trống hoặc = 1 → fill leaveTypeId đầu tiên trong danh sách cho chọn
  useEffect(() => {
    if (!open) return;
    const firstId = selectableLeaveTypes[0]?.leaveTypeId;
    if (firstId == null) return;

    setFormData((prev) => {
      const currentId = Number(prev.leaveType);
      if (prev.leaveType != null && prev.leaveType !== "" && currentId !== 1) {
        return prev;
      }
      if (currentId === firstId) return prev;
      return { ...prev, leaveType: firstId };
    });
  }, [open, selectableLeaveTypes, formData.leaveType]);

  const toDate = (v) => {
    if (!v) return new Date(NaN);
    if (dayjs.isDayjs(v)) return v.toDate();
    if (v instanceof Date) return v;
    return new Date(v);
  };

  const parsePayloadData = (value) => {
    if (!value) return {};
    if (typeof value === "string") {
      try {
        return JSON.parse(value) || {};
      } catch {
        return {};
      }
    }
    return value;
  };

  const combineDateAndTime = useCallback(
    (dateInput, timeStr) => {
      if (!dateInput || !timeStr) return null;

      const [hh, mm, ss] = timeStr.split(":").map(Number);

      return dayjs(dateInput)
        .tz(genaralSettingTimeZone, true)
        .hour(hh)
        .minute(mm)
        .second(ss || 0)
        .millisecond(0)
        .toDate();
    },
    [genaralSettingTimeZone]
  );

  const applyShiftToDate = useCallback(
    (baseDate, shift) => {
      if (!shift) {
        return {
          start: combineDateAndTime(baseDate, "00:00:00"),
          end: combineDateAndTime(baseDate, "23:59:00"),
        };
      }

      const start = combineDateAndTime(baseDate, shift.shiftStart);
      let end = combineDateAndTime(baseDate, shift.shiftEnd);

      if (shift.shiftEnd === "00:00:00" || isBefore(end, start)) {
        end = dayjs(end).add(1, "day").toDate();
      }

      return { start, end };
    },
    [combineDateAndTime]
  );

  const applyShiftToPeriod = useCallback(
    (periodStart, periodEnd, shift) => {
      const startDay = startOfDay(toDate(periodStart));
      const endDay = startOfDay(toDate(periodEnd || periodStart));

      if (!shift) {
        return {
          start: combineDateAndTime(startDay, "00:00:00"),
          end: combineDateAndTime(endDay, "23:59:00"),
        };
      }

      const start = combineDateAndTime(startDay, shift.shiftStart);
      let end = combineDateAndTime(endDay, shift.shiftEnd);
      const isSameDay = startDay.getTime() === endDay.getTime();

      if (isSameDay) {
        if (shift.shiftEnd === "00:00:00" || isBefore(end, start)) {
          end = dayjs(end).add(1, "day").toDate();
        }
      } else if (shift.shiftEnd === "00:00:00") {
        end = dayjs(endDay).add(1, "day").toDate();
      }

      return { start, end };
    },
    [combineDateAndTime]
  );

  const createDefaultPeriod = useCallback(
    (shift = sortedShifts[0], startDate = null, endDate = null) => {
      const baseDate = startOfDay(toDate(startDate || selectedDate));
      const { start, end } = applyShiftToDate(baseDate, shift);
      return {
        id: createPeriodId(),
        shiftId: shift?.shiftId ?? null,
        startDate: startDate ? toDate(startDate) : start,
        endDate: endDate ? toDate(endDate) : end,
      };
    },
    [sortedShifts, selectedDate, applyShiftToDate]
  );

  // Request mode: lấy detail request để fill dữ liệu mới nhất từ newData
  useEffect(() => {
    if (!open) return;
    if (isLeaveMode) return;
    if (!sortedShifts.length) return;
    if (!leaveInfo.leaveRequestId && !leaveInfo.leaveId) return;

    const fetchLeaveRequestDetail = async () => {
      try {
        show();
        const response = await getLeaveRequestId({
          leaveRequestId: leaveInfo.leaveRequestId ?? leaveInfo.leaveId,
          langId: localStorage.getItem("i18nextLngId"),
        });

        const detail = response?.data?.data?.[0];
        if (!detail) return;

        const newData = parsePayloadData(detail.newData);
        const start = toDateForPicker(newData.start || detail.start, dateTimeFormat);
        const end = toDateForPicker(newData.end || detail.end, dateTimeFormat);
        const defaultShift =
          sortedShifts.find((s) => String(s.shiftId) === String(newData.shiftId)) ||
          sortedShifts[0];

        setFormData((prev) => ({
          ...prev,
          userId: newData.userId ?? detail.userId ?? leaveInfo.userId,
          selectUser: detail.fullName || detail.name || leaveInfo.name,
          leaveType:
            newData.leaveTypeId != null || detail.leaveTypeId != null
              ? Number(newData.leaveTypeId ?? detail.leaveTypeId)
              : prev.leaveType,
          requestReason: detail.requestReason || newData.requestReason || "",
          approveReason: detail.responseReason || newData.responseReason || "",
          approvedFrom: start,
          approvedTo: end,
          approvalStatusId: detail.requestStatusId ?? detail.approvalStatusId,
        }));
        setLeavePeriods([createDefaultPeriod(defaultShift, start, end)]);
        setDatepickerKey((k) => (k || 0) + 1);
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        hide();
      }
    };

    fetchLeaveRequestDetail();
  }, [open, isLeaveMode, sortedShifts, leaveInfo, dateTimeFormat, createDefaultPeriod]);

  useEffect(() => {
    if (!open || !isLeaveMode) return;
    if (!leaveInfo.leaveId || !sortedShifts.length) return;

    const fetchLeaveDetail = async () => {
      try {
        setOldLeaveData(null);
        show();
        const response = await getLeaveById({
          leaveId: leaveInfo.leaveId,
          langId: localStorage.getItem("i18nextLngId"),
        });

        const data = response?.data?.data?.[0];
        if (!data) return;

        const start = toDateForPicker(data.requestStartDate, dateTimeFormat);
        const end = toDateForPicker(data.requestEndDate, dateTimeFormat);
        const defaultShift =
          sortedShifts.find((s) => String(s.shiftId) === String(data.shiftId)) ||
          sortedShifts[0];

        setOldLeaveData(data);
        setFormData((prev) => ({
          ...prev,
          userId: data.userId ?? leaveInfo.userId,
          selectUser: data.fullName || data.name || leaveInfo.name,
          leaveType:
            data.leaveTypeId != null ? Number(data.leaveTypeId) : prev.leaveType,
          requestReason: data.requestReason || "",
          approveReason: data.responseReason || "",
          approvedFrom: start,
          approvedTo: end,
          approvalStatusId: data.approvalStatusId,
        }));
        setLeavePeriods([createDefaultPeriod(defaultShift, start, end)]);
        setDatepickerKey((k) => (k || 0) + 1);
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        hide();
      }
    };

    fetchLeaveDetail();
  }, [open, isLeaveMode, leaveInfo.leaveId, sortedShifts, dateTimeFormat, createDefaultPeriod]);

  const editingLeaveAdjustment = useMemo(() => {
    // Chỉ dùng khi TÍNH số phép còn lại (cộng lại ngày của leave/request đang sửa
    // vì đã nằm trong usedFromYear). Không dùng để hiển thị usedFromYear.
    const originalLeave = isLeaveMode ? oldLeaveData || leaveInfo : leaveInfo;
    const originalTypeId = Number(originalLeave?.leaveTypeId);
    const currentTypeId = Number(formData.leaveType);

    if (!originalTypeId || originalTypeId !== currentTypeId) return 0;
    // leaveTypeId = 1 (không lương) không trừ phép năm
    if (originalTypeId === 1) return 0;

    // Chỉ tính từ numOfTime + hoursPerLeaveDaySnapshot; không còn fallback numOfDay
    if (!originalLeave?.numOfTime) return 0;

    const snapshot = normalizeHoursPerLeaveDay(
      originalLeave?.hoursPerLeaveDaySnapshot ||
      (leaveTypesData || []).find(
        (item) => Number(item.leaveTypeId) === originalTypeId
      )?.hoursPerLeaveDay ||
      "08:00:00"
    );
    const dayMinutes =
      parseTimeStringToMinutes(snapshot) || DEFAULT_FULL_DAY_MINUTES;
    const timeMinutes = parseTimeStringToMinutes(originalLeave.numOfTime);
    return dayMinutes > 0 ? roundNumOfDay(timeMinutes / dayMinutes) : 0;
  }, [isLeaveMode, oldLeaveData, leaveInfo, formData.leaveType, leaveTypesData]);

  const leaveEntitlementInfo = useMemo(() => {
    if (!leaveByUserByLeave?.length) {
      return { text: "—", usedDays: null, details: [] };
    }
    const details = buildUsedFromYearDetails(leaveByUserByLeave);
    const usedDaysExact = sumUsedFromYearDays(leaveByUserByLeave);
    const usedDays = formatOneDecimal(usedDaysExact);
    const totalDays = formatDecimalNumber(
      leaveByUserByLeave[0].totalEntitlementYear
    );
    const availability = formatLeaveEntitlementAvailabilityText(
      leaveByUserByLeave,
      t
    );
    return {
      text: `${usedDays}/${totalDays} (${availability})`,
      usedDays,
      details,
    };
  }, [leaveByUserByLeave, t]);

  const fetchLeaveByUserData = useCallback(async (userId) => {
    if (!userId) return;

    const payload = {
      langId: localStorage.getItem("i18nextLngId"),
      userId,
      year: new Date().getFullYear(),
    };

    try {
      const response = await getLeaveByUser(payload);
      if (
        response?.data?.message === "Successful" ||
        response?.data?.message === "Thành công"
      ) {
        setLeaveByUserData(response.data.data ?? []);
      }
    } catch (error) {
      if (error?.response && error.response.status !== 401) {
        toast.error(error?.response?.data?.message || "Error");
      } else {
        toast.error(t("common.network-timeout"));
      }
    }
  }, [t]);

  const fetchLeaveByUserByLeaveId = useCallback(
    async (userId, leaveTypeId, year) => {
      if (!userId || !leaveTypeId) return;

      const payload = {
        langId: localStorage.getItem("i18nextLngId"),
        userId,
        leaveTypeId,
        year: year || new Date().getFullYear(),
      };

      show();
      setLeaveByUserByLeaveData([]);
      setLoadingLeaveEntitlement(true);

      try {
        const response = await getLeaveByUserByLeaveId(payload);
        if (
          response?.data?.message === "Successful" ||
          response?.data?.message === "Thành công"
        ) {
          setLeaveByUserByLeaveData(normalizeLeaveBalanceData(response.data.data));
        }
      } catch (error) {
        if (error?.response && error.response.status !== 401) {
          toast.error(error?.response?.data?.message || "Error");
        } else {
          toast.error(t("common.network-timeout"));
        }
      } finally {
        setLoadingLeaveEntitlement(false);
        hide();
      }
    },
    [t, show, hide]
  );

  useEffect(() => {
    if (!open) return;
    if (!formData.userId) return;
    fetchLeaveByUserData(formData.userId);
  }, [open, formData.userId, fetchLeaveByUserData]);

  useEffect(() => {
    if (!open) return;
    if (!formData.userId || !formData.leaveType) return;

    // Có 2 năm → lấy năm end
    const period = leavePeriods[0];
    const startYear = period?.startDate
      ? toDate(period.startDate).getFullYear()
      : null;
    const endYear = period?.endDate
      ? toDate(period.endDate).getFullYear()
      : null;
    const year =
      endYear != null && startYear != null && endYear !== startYear
        ? endYear
        : endYear ?? startYear ?? new Date().getFullYear();

    fetchLeaveByUserByLeaveId(formData.userId, formData.leaveType, year);
  }, [
    open,
    formData.userId,
    formData.leaveType,
    leavePeriods,
    fetchLeaveByUserByLeaveId,
  ]);

  const syncFormDatesFromPeriods = (periods) => {
    const validPeriods = periods.filter((p) => p.startDate && p.endDate);
    if (!validPeriods.length) return { requestStartDate: null, requestEndDate: null };

    const starts = validPeriods.map((p) => toDate(p.startDate).getTime());
    const ends = validPeriods.map((p) => toDate(p.endDate).getTime());

    return {
      requestStartDate: new Date(Math.min(...starts)),
      requestEndDate: new Date(Math.max(...ends)),
    };
  };

  const toUTCByClientTimezone = (value) => {
    if (!value) return null;
    return dayjs(value).tz(dayjs.tz.guess(), true).utc().toISOString();
  };

  const buildLeaveDataSnapshot = (data, hoursPerLeaveDaySnapshot) => {
    if (!data) return null;

    const start = toDateForPicker(data.start || data.requestStartDate, dateTimeFormat);
    const end = toDateForPicker(data.end || data.requestEndDate, dateTimeFormat);

    const snapshot = normalizeHoursPerLeaveDay(
      data.hoursPerLeaveDaySnapshot || hoursPerLeaveDaySnapshot || "08:00:00"
    );
    const hoursPerLeaveDayMinutes =
      parseTimeStringToMinutes(snapshot) || DEFAULT_FULL_DAY_MINUTES;

    // Ưu tiên numOfTime từ API; nếu leave cũ chỉ có numOfDay (dữ liệu lịch sử)
    // thì migrate sang HH:mm:ss khi đọc — output luôn dùng field numOfTime*, không trả về numOfDay
    const toTimeField = (timeValue, legacyDayValue) => {
      if (timeValue) return normalizeHoursPerLeaveDay(timeValue);
      if (legacyDayValue != null) {
        return daysToHhMmSs(Number(legacyDayValue) || 0, hoursPerLeaveDayMinutes);
      }
      return "00:00:00";
    };

    return {
      leaveId: Number(data.leaveId ?? leaveInfo.leaveId),
      userId: Number(data.userId ?? leaveInfo.userId),
      shiftId: data.shiftId != null ? Number(data.shiftId) : null,
      start: data.start || (start ? toUTCByClientTimezone(start) : null),
      end: data.end || (end ? toUTCByClientTimezone(end) : null),
      leaveTypeId: Number(data.leaveTypeId ?? formData.leaveType),
      leavePolicyId: null,
      numOfTime: toTimeField(data.numOfTime, data.numOfDay),
      numOfTimeStart: toTimeField(data.numOfTimeStart, data.numOfDayStart),
      numOfTimeCarryStart: toTimeField(
        data.numOfTimeCarryStart,
        data.numOfDayCarryStart
      ),
      numOfTimeEnd: toTimeField(data.numOfTimeEnd, data.numOfDayEnd),
      numOfTimeCarryEnd: toTimeField(
        data.numOfTimeCarryEnd,
        data.numOfDayCarryEnd
      ),
      hoursPerLeaveDaySnapshot: snapshot,
      requestReason: data.requestReason || "",
      responseReason: data.responseReason || "",
    };
  };

  const handleCloseDialog = () => {
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (
      (name === "requestReason" || name === "approveReason") &&
      value.length > MAX_REASON_LENGTH
    ) {
      return;
    }

    if (name === "leaveType") {
      // Không cho chọn leaveTypeId = 1 (AR / không lương theo giờ)
      if (Number(value) === 1) return;
      setLeaveByUserByLeaveData([]);
      setFormData((prev) => ({
        ...prev,
        leaveType: Number(value),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePeriodShiftChange = (periodId, shiftId) => {
    const selectedShift = sortedShifts.find(
      (s) => String(s.shiftId) === String(shiftId)
    );

    setLeavePeriods((prev) =>
      prev.map((period) => {
        if (period.id !== periodId) return period;
        if (!selectedShift) return { ...period, shiftId };

        const { start, end } =
          period.startDate && period.endDate
            ? applyShiftToPeriod(
              period.startDate,
              period.endDate,
              selectedShift
            )
            : applyShiftToDate(
              startOfDay(toDate(period.startDate || selectedDate)),
              selectedShift
            );

        return {
          ...period,
          shiftId: selectedShift.shiftId,
          startDate: start,
          endDate: end,
        };
      })
    );
  };

  const handlePeriodDateChange = (periodId, startDate, endDate) => {
    setLeavePeriods((prev) =>
      prev.map((period) =>
        period.id === periodId
          ? { ...period, startDate: toDate(startDate), endDate: toDate(endDate) }
          : period
      )
    );
  };

  const getShiftForPeriod = (period) =>
    sortedShifts.find((s) => String(s.shiftId) === String(period.shiftId));

  /** Lấy hoursPerLeaveDay của leaveType (snapshot gửi API) */
  const resolveHoursPerLeaveDaySnapshot = useCallback(
    (leaveTypeId) => {
      const fromTypes = (leaveTypesData || []).find(
        (item) => Number(item.leaveTypeId) === Number(leaveTypeId)
      );
      const fromBalance = (leaveByUser || []).find(
        (item) => Number(item.leaveTypeId) === Number(leaveTypeId)
      );
      return normalizeHoursPerLeaveDay(
        fromTypes?.hoursPerLeaveDay ??
        fromBalance?.hoursPerLeaveDay ??
        fromBalance?.hoursPerLeaveDaySnapshot ??
        "08:00:00"
      );
    },
    [leaveTypesData, leaveByUser]
  );

  // 1 ngày phép = hoursPerLeaveDay của leaveType đang chọn (không theo độ dài ca)
  const minutesPerDay = useMemo(() => {
    const snapshot = resolveHoursPerLeaveDaySnapshot(formData.leaveType);
    return parseTimeStringToMinutes(snapshot) || DEFAULT_FULL_DAY_MINUTES;
  }, [formData.leaveType, resolveHoursPerLeaveDaySnapshot]);

  const hoursOneDayLabel = useMemo(
    () => formatHoursOneDayLabel(minutesPerDay / 60),
    [minutesPerDay]
  );

  useEffect(() => {
    const allValid = leavePeriods.every((p) => {
      if (!p.startDate || !p.endDate) return false;
      return !isBefore(toDate(p.endDate), toDate(p.startDate));
    });
    setIsInputValid(allValid && leavePeriods.length > 0);
  }, [leavePeriods]);

  const carryLeaveText = useMemo(() => {
    if (!leaveByUserByLeave?.length) return null;

    const item = leaveByUserByLeave[0];
    if (!item.isCarry) return null;

    if (item.expiredDt) {
      const now = new Date();
      const expiredDate = new Date(item.expiredDt);
      now.setHours(0, 0, 0, 0);
      expiredDate.setHours(0, 0, 0, 0);
      if (now > expiredDate) return null;
    }

    const expired = item.expiredDt
      ? format(new Date(item.expiredDt), genaralSettingDateFormat)
      : "";
    const hoursPerDay = resolveItemHoursPerLeaveDay(item);

    return `${formatLeaveBalanceDisplay(item.usedFromCarry, hoursPerDay)}/${formatLeaveBalanceDisplay(item.carryIn, hoursPerDay)} (${t("common.expire-on")} ${expired})`;
  }, [leaveByUserByLeave, t, genaralSettingDateFormat]);

  const isDeductLeaveType = false;

  const leaveLimits = useMemo(() => {
    const item =
      leaveByUserByLeave?.[0] ||
      leaveByUser.find(
        (l) => Number(l.leaveTypeId) === Number(formData.leaveType)
      );

    if (!item) return null;

    return {
      maxLeaveDuration: Number(item.maxLeaveDuration),
      isMonthlyLimit: Boolean(item.isMonthlyLimit),
      monthlyLimitDays: Number(item.monthlyLimitDays),
      isWeeklyLimit: Boolean(item.isWeeklyLimit),
      weeklyLimitDays: Number(item.weeklyLimitDays),
    };
  }, [leaveByUserByLeave, leaveByUser, formData.leaveType]);

  // Duration / tổng phút đã cap theo hoursPerLeaveDay / ngày lịch
  const periodDurations = useMemo(() => {
    return leavePeriods.map((period) => {
      const shift = getShiftForPeriod(period);
      const days = getPeriodDayBreakdown(
        period.startDate,
        period.endDate,
        shift,
        combineDateAndTime,
        minutesPerDay
      );
      const minutes = days.reduce((s, d) => s + (Number(d.minutes) || 0), 0);
      return minutesToDurationParts(minutes, minutesPerDay);
    });
  }, [leavePeriods, sortedShifts, combineDateAndTime, minutesPerDay]);

  const totalRequestedMinutes = useMemo(() => {
    return leavePeriods.reduce((sum, period) => {
      const shift = getShiftForPeriod(period);
      const days = getPeriodDayBreakdown(
        period.startDate,
        period.endDate,
        shift,
        combineDateAndTime,
        minutesPerDay
      );
      return sum + days.reduce((s, d) => s + (Number(d.minutes) || 0), 0);
    }, 0);
  }, [leavePeriods, sortedShifts, combineDateAndTime, minutesPerDay]);

  const totalRequestedDays = useMemo(() => {
    return roundNumOfDay(
      leavePeriods.reduce((sum, period) => {
        const shift = getShiftForPeriod(period);
        const days = getPeriodDayBreakdown(
          period.startDate,
          period.endDate,
          shift,
          combineDateAndTime,
          minutesPerDay
        );

        return sum + days.reduce((daySum, day) => daySum + day.dayUnits, 0);
      }, 0)
    );
  }, [leavePeriods, sortedShifts, combineDateAndTime, minutesPerDay]);

  const leaveAllocation = useMemo(() => {
    if (!isDeductLeaveType || !leaveByUserByLeave?.length) return null;
    if (totalRequestedMinutes <= 0) return null;

    const item = leaveByUserByLeave[0];
    const usedDays = Math.max(
      sumUsedFromYearDays(leaveByUserByLeave) - editingLeaveAdjustment,
      0
    );
    const totalEntitlement = Number(item.totalEntitlementYear) || 0;
    const lateEarlyMinutes = parseTimeStringToMinutes(lateEarlyUsedTime);

    return calculateLeaveAllocation({
      totalMinutes: totalRequestedMinutes,
      minutesPerDay,
      usedDays,
      totalEntitlement,
      lateEarlyMinutes,
    });
  }, [
    isDeductLeaveType,
    leaveByUserByLeave,
    totalRequestedMinutes,
    minutesPerDay,
    lateEarlyUsedTime,
    editingLeaveAdjustment,
  ]);

  const totalRequestedParts = useMemo(
    () => minutesToDurationParts(totalRequestedMinutes, minutesPerDay),
    [totalRequestedMinutes, minutesPerDay]
  );

  const nonDeductEntitlement = useMemo(() => {
    if (isDeductLeaveType || formData.leaveType === 1) return null;
    if (!leaveByUserByLeave?.length || totalRequestedDays <= 0) return null;

    const item = leaveByUserByLeave[0];
    const usedFromYearRaw = sumUsedFromYearDays(leaveByUserByLeave);
    const primaryBalance = getRemainingBalanceFromEntitlement(
      leaveByUserByLeave,
      editingLeaveAdjustment
    );
    const maxDays = primaryBalance.remainingPaidDays;
    const maxMinutes = Math.round(maxDays * minutesPerDay);
    const paidMinutes = Math.min(totalRequestedMinutes, maxMinutes);
    const excessMinutes = Math.max(totalRequestedMinutes - maxMinutes, 0);

    const deductLeaveTypeId = Number(item.overflowLeaveTypeId);
    const canUseDeductLeave =
      Number.isFinite(deductLeaveTypeId) &&
      deductLeaveTypeId > 0 &&
      deductLeaveTypeId !== 1 &&
      deductLeaveTypeId !== Number(formData.leaveType);

    let bridgeMinutes = 0;
    let unpaidMinutes = excessMinutes;

    if (canUseDeductLeave && excessMinutes > 0) {
      const deductBalance = leaveByUser.filter(
        (l) => Number(l.leaveTypeId) === deductLeaveTypeId
      );
      // Chỉ tính adjustment từ numOfTime + hoursPerLeaveDaySnapshot; không fallback numOfDay
      const originalLeaveForDeductCalc = isLeaveMode
        ? oldLeaveData || leaveInfo
        : leaveInfo;
      const deductAdjustment =
        Number(originalLeaveForDeductCalc?.leaveTypeId) === deductLeaveTypeId &&
          originalLeaveForDeductCalc?.numOfTime
          ? roundNumOfDay(
            parseTimeStringToMinutes(originalLeaveForDeductCalc.numOfTime) /
            (parseTimeStringToMinutes(
              resolveHoursPerLeaveDaySnapshot(deductLeaveTypeId)
            ) || DEFAULT_FULL_DAY_MINUTES)
          )
          : 0;
      const deductRemaining = getRemainingBalanceFromEntitlement(
        deductBalance,
        deductAdjustment
      );
      const deductMaxMinutes = Math.round(
        deductRemaining.remainingPaidDays * minutesPerDay
      );
      bridgeMinutes = Math.min(excessMinutes, deductMaxMinutes);
      unpaidMinutes = Math.max(excessMinutes - bridgeMinutes, 0);
    }

    const remainingAfterMinutes = Math.max(maxMinutes - totalRequestedMinutes, 0);
    const isExceeded = excessMinutes > 0;

    return {
      isExceeded,
      maxDays,
      totalEntitlement: Number(item.totalEntitlementYear) || 0,
      usedDays: usedFromYearRaw,
      bridgeMinutes,
      bridgeDays: roundNumOfDay(bridgeMinutes / minutesPerDay),
      deductLeaveTypeId: canUseDeductLeave ? deductLeaveTypeId : null,
      requestedLabel: formatDurationLabel(
        minutesToDurationParts(totalRequestedMinutes, minutesPerDay),
        t
      ),
      paidLabel: formatDurationLabel(
        minutesToDurationParts(paidMinutes, minutesPerDay),
        t
      ),
      bridgeLabel: formatDurationLabel(
        minutesToDurationParts(bridgeMinutes, minutesPerDay),
        t
      ),
      unpaidLabel: formatDurationLabel(
        minutesToDurationParts(unpaidMinutes, minutesPerDay),
        t
      ),
      timeLeftLabel: isExceeded
        ? formatDurationLabel(
          minutesToDurationParts(unpaidMinutes, minutesPerDay),
          t
        )
        : formatDurationLabel(
          minutesToDurationParts(remainingAfterMinutes, minutesPerDay),
          t
        ),
    };
  }, [
    isDeductLeaveType,
    formData.leaveType,
    leaveByUserByLeave,
    leaveByUser,
    leaveToBeDeducted,
    totalRequestedDays,
    totalRequestedMinutes,
    minutesPerDay,
    t,
    editingLeaveAdjustment,
    isLeaveMode,
    oldLeaveData,
    leaveInfo,
    resolveHoursPerLeaveDaySnapshot,
  ]);

  const selectedLeaveTypeName = useMemo(
    () => getLeaveTypeDisplayName(leaveTypesData, formData.leaveType),
    [leaveTypesData, formData.leaveType]
  );

  const deductLeaveTypeName = useMemo(
    () =>
      nonDeductEntitlement?.deductLeaveTypeId
        ? getLeaveTypeDisplayName(
          leaveTypesData,
          nonDeductEntitlement.deductLeaveTypeId
        )
        : "",
    [leaveTypesData, nonDeductEntitlement?.deductLeaveTypeId]
  );

  /** Summary thống nhất cho mọi loại phép */
  const leaveSummary = useMemo(() => {
    if (totalRequestedMinutes <= 0) return null;

    // leaveTypeId = 1 (AR / không lương theo giờ): toàn bộ tính unpaid
    if (Number(formData.leaveType) === 1) {
      const requestedLabel = formatDurationLabel(totalRequestedParts, t);
      const zeroLabel = formatDurationLabel(
        { days: 0, hours: 0, minutes: 0, seconds: 0 },
        t
      );

      return {
        hasUnpaid: true,
        hasBridge: false,
        requestedLabel,
        paidLabel: zeroLabel,
        bridgeLabel: null,
        unpaidLabel: requestedLabel,
        timeLeftLabel: requestedLabel,
        footerType: null,
        footerData: null,
      };
    }

    if (isDeductLeaveType) {
      if (!leaveAllocation) return null;

      const hasUnpaid =
        leaveAllocation.unpaid.days > 0 ||
        leaveAllocation.unpaid.hours > 0 ||
        leaveAllocation.unpaid.minutes > 0 ||
        leaveAllocation.unpaid.seconds > 0;

      return {
        hasUnpaid,
        hasBridge: false,
        requestedLabel: formatDurationLabel(totalRequestedParts, t),
        paidLabel: formatDurationLabel(leaveAllocation.paid, t),
        bridgeLabel: null,
        unpaidLabel: formatDurationLabel(leaveAllocation.unpaid, t),
        timeLeftLabel: formatDurationLabel(leaveAllocation.timeLeft, t),
        footerType: hasUnpaid ? "deduct" : null,
        footerData: hasUnpaid
          ? {
            balance: leaveAllocation.availableDays,
            unpaid: leaveAllocation.unpaid.days,
          }
          : null,
      };
    }

    // Chưa load được quyền lợi phép: vẫn hiện tổng ngày yêu cầu (paid/unpaid tạm = 0 / full)
    if (!nonDeductEntitlement) {
      const requestedLabel = formatDurationLabel(totalRequestedParts, t);
      const zeroLabel = formatDurationLabel(
        { days: 0, hours: 0, minutes: 0, seconds: 0 },
        t
      );

      return {
        hasUnpaid: false,
        hasBridge: false,
        requestedLabel,
        paidLabel: requestedLabel,
        bridgeLabel: null,
        unpaidLabel: zeroLabel,
        timeLeftLabel: zeroLabel,
        footerType: null,
        footerData: null,
      };
    }

    const { isExceeded, bridgeMinutes = 0 } = nonDeductEntitlement;
    const hasBridge = bridgeMinutes > 0;
    return {
      hasUnpaid: isExceeded,
      hasBridge,
      requestedLabel: nonDeductEntitlement.requestedLabel,
      paidLabel: nonDeductEntitlement.paidLabel,
      bridgeLabel: hasBridge ? nonDeductEntitlement.bridgeLabel : null,
      unpaidLabel: nonDeductEntitlement.unpaidLabel,
      timeLeftLabel: nonDeductEntitlement.timeLeftLabel,
      footerType: isExceeded
        ? hasBridge
          ? "bridge"
          : "non-deduct"
        : null,
      footerData: isExceeded
        ? {
          ...nonDeductEntitlement,
          balance: nonDeductEntitlement.maxDays,
          deductLeaveType: deductLeaveTypeName,
        }
        : null,
    };
  }, [
    formData.leaveType,
    totalRequestedMinutes,
    isDeductLeaveType,
    leaveAllocation,
    nonDeductEntitlement,
    totalRequestedParts,
    deductLeaveTypeName,
    t,
  ]);

  const handleSubmit = async () => {
    const period = leavePeriods[0];
    if (!period?.startDate || !period?.endDate) {
      toast.warn(t("common.invalid-date"));
      return;
    }

    if (isBefore(toDate(period.endDate), toDate(period.startDate))) {
      toast.warn(t("common.invalid-date"));
      return;
    }

    const leaveBalance = leaveByUserByLeave?.[0];
    const usedFromYear = Math.max(
      sumUsedFromYearDays(leaveByUserByLeave) - editingLeaveAdjustment,
      0
    );
    const totalEntitlementYear = Number(leaveBalance?.totalEntitlementYear) || 0;

    let carryAvailable = 0;
    if (leaveBalance?.isCarry) {
      const expiredOk = !leaveBalance.expiredDt || (() => {
        const now = new Date();
        const expiredDate = new Date(leaveBalance.expiredDt);
        now.setHours(0, 0, 0, 0);
        expiredDate.setHours(0, 0, 0, 0);
        return now <= expiredDate;
      })();

      if (expiredOk) {
        carryAvailable = Math.max(
          (Number(leaveBalance.carryIn) || 0) - sumUsedFromCarryDays(leaveByUserByLeave),
          0
        );
      }
    }

    const shift = getShiftForPeriod(period);
    const dayBreakdown = getPeriodDayBreakdown(
      period.startDate,
      period.endDate,
      shift,
      combineDateAndTime,
      minutesPerDay
    );

    if (!dayBreakdown.length) {
      toast.warn(t("common.invalid-date"));
      return;
    }

    const requestReason = formData.requestReason || "";
    const isHourlyLeaveType = Number(formData.leaveType) === 1;
    const selectedLeaveSnapshot = resolveHoursPerLeaveDaySnapshot(formData.leaveType);
    let remainingPaidDays = isHourlyLeaveType
      ? Number.POSITIVE_INFINITY
      : Math.max(totalEntitlementYear - usedFromYear, 0) + carryAvailable;
    let remainingCarry = isHourlyLeaveType ? 0 : carryAvailable;

    // Ưu tiên overflowLeaveTypeId của loại phép đang chọn (khớp summary UI);
    // fallback setting công ty leaveToBeDeducted nếu chưa cấu hình overflow
    const selectedLeaveTypeConfig = (leaveTypesData || []).find(
      (lt) => Number(lt.leaveTypeId) === Number(formData.leaveType)
    );
    const overflowFromEntitlement = Number(leaveBalance?.overflowLeaveTypeId);
    const overflowFromLeaveType = Number(
      selectedLeaveTypeConfig?.overflowLeaveTypeId
    );
    const deductLeaveTypeId = [
      overflowFromEntitlement,
      overflowFromLeaveType,
      Number(leaveToBeDeducted),
    ].find((id) => Number.isFinite(id) && id > 0) ?? 0;
    const canUseDeductLeave =
      !isHourlyLeaveType &&
      Number.isFinite(deductLeaveTypeId) &&
      deductLeaveTypeId > 0 &&
      deductLeaveTypeId !== 1 &&
      deductLeaveTypeId !== Number(formData.leaveType);

    let remainingDeductDays = 0;
    let remainingDeductCarry = 0;
    if (canUseDeductLeave) {
      const deductBalance = leaveByUser.filter(
        (item) => Number(item.leaveTypeId) === deductLeaveTypeId
      );
      // Cộng lại ngày đang sửa nếu leave gốc cũng là loại leaveToBeDeducted
      const originalLeaveForDeduct = isLeaveMode
        ? oldLeaveData || leaveInfo
        : leaveInfo;
      // Chỉ tính adjustment từ numOfTime + hoursPerLeaveDaySnapshot; không fallback numOfDay
      let deductAdjustment = 0;
      if (
        Number(originalLeaveForDeduct?.leaveTypeId) === deductLeaveTypeId &&
        originalLeaveForDeduct?.numOfTime
      ) {
        const snap = resolveHoursPerLeaveDaySnapshot(deductLeaveTypeId);
        const dayMins =
          parseTimeStringToMinutes(snap) || DEFAULT_FULL_DAY_MINUTES;
        deductAdjustment = roundNumOfDay(
          parseTimeStringToMinutes(originalLeaveForDeduct.numOfTime) /
          dayMins
        );
      }
      const deductRemaining = getRemainingBalanceFromEntitlement(
        deductBalance,
        deductAdjustment
      );
      remainingDeductDays = deductRemaining.remainingPaidDays;
      remainingDeductCarry = deductRemaining.carryAvailable;
    }

    const { paidDays, unpaidDays: excessDays } = splitDaysByPaidBalance(
      dayBreakdown,
      remainingPaidDays,
      shift,
      combineDateAndTime,
      minutesPerDay
    );

    let bridgeDays = [];
    let unpaidDays = [];
    if (excessDays.length && !isHourlyLeaveType) {
      const deductSnapshot = resolveHoursPerLeaveDaySnapshot(deductLeaveTypeId);
      const deductMinutesPerDay =
        parseTimeStringToMinutes(deductSnapshot) || DEFAULT_FULL_DAY_MINUTES;
      const remappedExcess = excessDays.map((d) => ({
        ...d,
        dayUnits: roundNumOfDay(
          (Number(d.minutes) || 0) / deductMinutesPerDay
        ),
      }));
      const splitExcess = splitExcessByDeductLeave(
        remappedExcess,
        canUseDeductLeave ? remainingDeductDays : 0,
        shift,
        combineDateAndTime,
        deductMinutesPerDay
      );
      bridgeDays = splitExcess.bridgeDays;
      unpaidDays = splitExcess.unpaidDays;
    } else if (isHourlyLeaveType) {
      unpaidDays = dayBreakdown;
    }

    // Edit leave (isLeaveMode): EDIT_LEAVE segment đầu + ADD_LEAVE các segment còn lại
    if (isLeaveMode) {
      const leaveRequests = [];
      const oldSnapshotBase = oldLeaveData || leaveInfo;

      const segments = [];
      if (paidDays.length) {
        segments.push({
          days: paidDays,
          leaveTypeId: formData.leaveType,
          carryAvailable: remainingCarry,
          hoursPerLeaveDaySnapshot: resolveHoursPerLeaveDaySnapshot(
            formData.leaveType
          ),
        });
      }
      if (bridgeDays.length) {
        segments.push({
          days: bridgeDays,
          leaveTypeId: deductLeaveTypeId,
          carryAvailable: remainingDeductCarry,
          hoursPerLeaveDaySnapshot:
            resolveHoursPerLeaveDaySnapshot(deductLeaveTypeId),
        });
      }
      if (unpaidDays.length && !isHourlyLeaveType) {
        segments.push({
          days: unpaidDays,
          leaveTypeId: 1,
          carryAvailable: 0,
          hoursPerLeaveDaySnapshot: selectedLeaveSnapshot,
        });
      }
      if (isHourlyLeaveType && unpaidDays.length) {
        segments.push({
          days: unpaidDays,
          leaveTypeId: 1,
          carryAvailable: 0,
          hoursPerLeaveDaySnapshot: selectedLeaveSnapshot,
        });
      }

      if (!segments.length) {
        toast.warn(t("common.invalid-date"));
        return;
      }

      segments.forEach((segment, index) => {
        const item = buildLeaveNewDataFromDays({
          days: segment.days,
          userId: formData.userId,
          shiftId: period.shiftId,
          leaveTypeId: segment.leaveTypeId,
          requestReason,
          carryAvailable: segment.carryAvailable,
          toUTC: toUTCByClientTimezone,
          leaveId: index === 0 ? leaveInfo.leaveId : undefined,
          hoursPerLeaveDaySnapshot: segment.hoursPerLeaveDaySnapshot,
        });
        if (!item) return;

        if (index === 0) {
          leaveRequests.push({
            requestType: "EDIT_LEAVE",
            leaveId: Number(leaveInfo.leaveId),
            userId: Number(formData.userId),
            start: item.startIso,
            end: item.endIso,
            requestReason,
            oldData: buildLeaveDataSnapshot(
              oldSnapshotBase,
              segment.hoursPerLeaveDaySnapshot
            ),
            newData: item.newData,
          });
        } else {
          leaveRequests.push({
            requestType: "ADD_LEAVE",
            userId: Number(formData.userId),
            start: item.startIso,
            end: item.endIso,
            requestReason,
            newData: item.newData,
          });
        }
      });

      if (!leaveRequests.length) {
        toast.warn(t("common.invalid-date"));
        return;
      }

      const payload = {
        leaveRequests,
        langId: localStorage.getItem("i18nextLngId"),
      };

      setIsSubmitting(true);
      try {
        const response = await addLeaveRequest(payload);
        if (
          response?.data?.message === "Successful" ||
          response?.data?.message === "Thành công"
        ) {
          handleCloseDialog();
          toast.success(t("common.edit-leave-request-successfully"));
          onRefresh?.();
        } else if (response.data.responseCode === 1085) {
          toast.warning(response?.data?.message);
          handleCloseDialog();
          onRefresh?.();
        } else {
          toast.error(response?.data?.message);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Edit leave request: cập nhật request hiện tại (segment đầu) + ADD_LEAVE phần còn lại
    const leaveRequestId = leaveInfo.leaveRequestId ?? leaveInfo.leaveId;
    const requestSegments = [];

    if (paidDays.length) {
      requestSegments.push({
        days: paidDays,
        leaveTypeId: formData.leaveType,
        carryAvailable: remainingCarry,
        hoursPerLeaveDaySnapshot: resolveHoursPerLeaveDaySnapshot(
          formData.leaveType
        ),
      });
    }
    if (bridgeDays.length) {
      requestSegments.push({
        days: bridgeDays,
        leaveTypeId: deductLeaveTypeId,
        carryAvailable: remainingDeductCarry,
        hoursPerLeaveDaySnapshot:
          resolveHoursPerLeaveDaySnapshot(deductLeaveTypeId),
      });
    }
    if (unpaidDays.length) {
      requestSegments.push({
        days: unpaidDays,
        leaveTypeId: 1,
        carryAvailable: 0,
        hoursPerLeaveDaySnapshot: selectedLeaveSnapshot,
      });
    }

    if (!requestSegments.length) {
      toast.warn(t("common.invalid-date"));
      return;
    }

    const editNewDataItem = buildLeaveNewDataFromDays({
      days: requestSegments[0].days,
      userId: formData.userId,
      shiftId: period.shiftId,
      leaveTypeId: requestSegments[0].leaveTypeId,
      requestReason,
      carryAvailable: requestSegments[0].carryAvailable,
      toUTC: toUTCByClientTimezone,
      hoursPerLeaveDaySnapshot: requestSegments[0].hoursPerLeaveDaySnapshot,
    });

    if (!editNewDataItem) {
      toast.warn(t("common.invalid-date"));
      return;
    }

    const editPayload = {
      leaveRequestId,
      requestReason,
      langId: localStorage.getItem("i18nextLngId"),
      newData: editNewDataItem.newData,
    };

    setIsSubmitting(true);
    try {
      const editResponse = await editLeaveRequest(editPayload);
      const editOk =
        editResponse?.data?.message === "Successful" ||
        editResponse?.data?.message === "Thành công" ||
        editResponse?.data?.responseCode === 1085;

      if (!editOk) {
        toast.error(editResponse?.data?.message);
        return;
      }

      const addSegments = requestSegments.slice(1);
      if (addSegments.length) {
        const leaveRequests = addSegments
          .map((segment) => {
            const unpaidItem = buildLeaveNewDataFromDays({
              days: segment.days,
              userId: formData.userId,
              shiftId: period.shiftId,
              leaveTypeId: segment.leaveTypeId,
              requestReason,
              carryAvailable: segment.carryAvailable,
              toUTC: toUTCByClientTimezone,
              hoursPerLeaveDaySnapshot: segment.hoursPerLeaveDaySnapshot,
            });
            if (!unpaidItem) return null;
            return {
              requestType: "ADD_LEAVE",
              userId: Number(formData.userId),
              start: unpaidItem.startIso,
              end: unpaidItem.endIso,
              requestReason,
              newData: unpaidItem.newData,
            };
          })
          .filter(Boolean);

        if (leaveRequests.length) {
          const addPayload = {
            leaveRequests,
            langId: localStorage.getItem("i18nextLngId"),
          };

          const addResponse = await addLeaveRequest(addPayload);
          const addOk =
            addResponse?.data?.message === "Successful" ||
            addResponse?.data?.message === "Thành công" ||
            addResponse?.data?.responseCode === 1085;

          if (!addOk) {
            toast.error(addResponse?.data?.message);
            return;
          }
        }
      }

      handleCloseDialog();
      if (editResponse?.data?.responseCode === 1085) {
        toast.warning(editResponse?.data?.message);
      } else {
        toast.success(t("common.edit-request-successfully"));
      }
      onRefresh?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!formData) {
    return null;
  }

  const oldLeaveType = oldLeaveData?.leaveTypeId ?? leaveInfo.leaveTypeId;
  const oldStart = toDateForPicker(
    oldLeaveData?.requestStartDate || leaveInfo.requestStartDate,
    dateTimeFormat
  );
  const oldEnd = toDateForPicker(
    oldLeaveData?.requestEndDate || leaveInfo.requestEndDate,
    dateTimeFormat
  );
  const oldDateRangeText =
    oldStart && oldEnd
      ? `${format(oldStart, dateTimeFormat)}  →  ${format(oldEnd, dateTimeFormat)}`
      : "";

  const isDialogLoading = loadingLeaveEntitlement || isSubmitting;

  return (
    <CustomDialog
      maxWidth="md"
      open={open}
      loading={isSubmitting || loadingLeaveEntitlement}
      onClose={handleCloseDialog}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold">
              {isLeaveMode ? t("common.edit-request-leave") : t("common.edit-leave")}
            </p>
          </div>
          <GrayIconButton
            onClick={handleCloseDialog}
            className="self-start"
            disabled={isDialogLoading}
          >
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>
      <DialogContent sx={{ mt: 0.5 }}>
        {/* Hàng 1: User */}
        <div
          className={`pt-[10px] grid grid-cols-1 gap-4 ${isLeaveMode ? "" : "md:grid-cols-2"
            }`}
        >
          <CustomInput
            required={false}
            title={t("common.leave-staff")}
            inputType="text"
            name="selectUser"
            value={formData.selectUser}
            readOnly
            className="w-full"
          />
          {!isLeaveMode && (
            <div>
              <label className="block text-sm text-[#171717] mb-2">
                {t("common.leave-type")} <span className="text-[#DC2626]">*</span>
              </label>
              <FormControl className="w-full">
                <CustomSelect
                  name="leaveType"
                  id="editLeaveType"
                  value={
                    formData.leaveType != null &&
                      formData.leaveType !== "" &&
                      Number(formData.leaveType) !== 1
                      ? Number(formData.leaveType)
                      : ""
                  }
                  onChange={handleInputChange}
                  options={selectableLeaveTypes}
                  dataType="leaveType"
                />
              </FormControl>
            </div>
          )}
        </div>

        {isLeaveMode && (
          <div className="pt-2">
            <label className="block text-sm text-[#171717] mb-2">
              {t("common.leave-type")} <span className="text-[#DC2626]">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)] gap-4 items-center">
              <FormControl className="w-full">
                <CustomSelect
                  name="oldLeaveType"
                  id="oldEditLeaveType"
                  value={oldLeaveType}
                  options={leaveTypesData}
                  dataType="leaveType"
                  readOnly
                />
              </FormControl>
              <div className="hidden md:flex justify-center text-2xl text-[#525252]">→</div>
              <FormControl className="w-full">
                <CustomSelect
                  name="leaveType"
                  id="editLeaveType"
                  value={
                    formData.leaveType != null &&
                      formData.leaveType !== "" &&
                      Number(formData.leaveType) !== 1
                      ? Number(formData.leaveType)
                      : ""
                  }
                  onChange={handleInputChange}
                  options={selectableLeaveTypes}
                  dataType="leaveType"
                />
              </FormControl>
            </div>
          </div>
        )}

        {/* Hàng 2: Quyền lợi phép; bên dưới: phép mang sang */}
        {formData.leaveType !== 1 && (!isLeaveMode || leaveByUserByLeave?.length > 0) && (
          <div className="pt-2 flex flex-col gap-4">
            <div>
              <div className="flex items-center gap-1">
                <label className="block text-sm">
                  {t("common.leave-entitlement")}
                </label>
                {leaveEntitlementInfo.details?.length ? (
                  <DefaultTooltip
                    title={t("common.leave-entitlement")}
                    readMore={false}
                    content={
                      <LeaveUsedDaysTooltipContent
                        details={leaveEntitlementInfo.details}
                        totalDays={leaveEntitlementInfo.usedDays}
                      />
                    }
                  >
                    <QuestionIcon16pxSvg className="fill-[#BDBDBD] shrink-0 w-3.5 h-3.5 cursor-help" />
                  </DefaultTooltip>
                ) : null}
              </div>
              <CustomInput
                required={false}
                title={t("common.leave-entitlement")}
                labelEnable={false}
                inputType="text"
                value={leaveEntitlementInfo.text}
                readOnly
                className="w-full"
                containerClassName="mt-2"
              />
            </div>
            {carryLeaveText && (
              <CustomInput
                required={false}
                title={t("common.carry-leave", {
                  year: leavePeriods[0]?.startDate
                    ? toDate(leavePeriods[0].startDate).getFullYear() - 1
                    : new Date().getFullYear() - 1,
                })}
                inputType="text"
                value={carryLeaveText}
                readOnly
                className="w-full"
              />
            )}
          </div>
        )}

        {leavePeriods.slice(0, 1).map((period, index) => {
          const durationLabel = formatDurationLabel(
            periodDurations[index] || {
              days: 0,
              hours: 0,
              minutes: 0,
              seconds: 0,
            },
            t
          );
          const periodShift = getShiftForPeriod(period);
          const shiftMinDateTime = periodShift
            ? combineDateAndTime(
              period.startDate || new Date(),
              periodShift.shiftStart || "00:00:00"
            )
            : undefined;
          const shiftMaxDateTime = periodShift
            ? combineDateAndTime(
              period.startDate || new Date(),
              periodShift.shiftEnd || "23:59:00"
            )
            : undefined;

          if (isLeaveMode) {
            return (
              <div key={period.id} className="mt-2 rounded-lg bg-[#F9FAFB] p-2 space-y-3">
                <div className="rounded-lg border border-[#D1D5DB] bg-white">
                  <div className="flex justify-between items-center mb-1 bg-[#F5F8FF] rounded-t-lg p-2">
                    <p className="text-sm font-semibold text-[#171717]">
                      {t("leave-request.period-label", { index: index + 1 })}
                    </p>
                    <span className="text-xs font-medium text-[#525252] bg-[#F5F5F5] rounded-md px-3 py-1">
                      {durationLabel}
                    </span>
                  </div>

                  <div className="space-y-2 rounded-b-lg bg-white p-2">
                    <div>
                      <label className="block text-sm text-[#171717] mb-2">
                        {t("common.shifts")} <span className="text-[#DC2626]">*</span>
                      </label>
                      <FormControl className="w-full">
                        <CustomSelect
                          name={`shift-${period.id}`}
                          value={period.shiftId}
                          onChange={(e) => handlePeriodShiftChange(period.id, e.target.value)}
                          options={sortedShifts}
                          dataType="shift"
                        />
                      </FormControl>
                    </div>

                    <div>
                      <label className="block text-sm text-[#171717] mb-2">
                        {t("common.date-time")}{" "}
                        <span className="text-[#DC2626]">*</span>
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)] gap-4 items-center">
                        <Tooltip
                          title={oldDateRangeText || ""}
                          disableHoverListener={!oldDateRangeText}
                          arrow
                          placement="top"
                        >
                          <span className="block w-full min-w-0">
                            <CustomInput
                              required={false}
                              inputType="text"
                              value={oldDateRangeText}
                              readOnly
                              labelEnable={false}
                              className="w-full"
                            />
                          </span>
                        </Tooltip>
                        <div className="hidden md:flex justify-center text-2xl text-[#525252]">
                          →
                        </div>
                        <CustomDateTimeRangePicker
                          key={`${datepickerKey}-${period.id}`}
                          value={[period.startDate, period.endDate]}
                          disableTime={false}
                          leaveLimits={leaveLimits}
                          minDateTime={shiftMinDateTime}
                          maxDateTime={shiftMaxDateTime}
                          onChange={([start, end]) => {
                            if (!start || !end) return;
                            handlePeriodDateChange(period.id, start, end);
                          }}
                          dateTimeFormat={dateTimeFormat}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={period.id} className="mt-2 rounded-lg bg-[#F9FAFB] p-2 space-y-3">
              <p className="text-sm font-semibold text-[#171717]">
                {t("leave-request.leave-periods-title")}
              </p>

              <div className="rounded-lg border border-[#D1D5DB] bg-white">
                <div className="flex justify-between items-center mb-1 bg-[#F5F8FF] rounded-t-lg p-2">
                  <p className="text-sm font-semibold text-[#171717]">
                    {t("leave-request.period-label", { index: index + 1 })}
                  </p>
                  <span className="text-xs font-medium text-[#525252] bg-[#F5F5F5] rounded-md px-3 py-1">
                    {durationLabel}
                  </span>
                </div>

                <div className="grid grid-cols-1 rounded-b-lg bg-white gap-4 items-end p-2">
                  <div>
                    <label className="block text-sm text-[#171717] mb-2">
                      {t("common.shifts")} <span className="text-[#DC2626]">*</span>
                    </label>
                    <FormControl className="w-full">
                      <CustomSelect
                        name={`shift-${period.id}`}
                        value={period.shiftId}
                        onChange={(e) => handlePeriodShiftChange(period.id, e.target.value)}
                        options={sortedShifts}
                        dataType="shift"
                      />
                    </FormControl>
                  </div>

                  <div>
                    <label className="block text-sm text-[#171717] mb-2">
                      {t("common.date-time")}{" "}
                      <span className="text-[#DC2626]">*</span>
                    </label>
                    <CustomDateTimeRangePicker
                      key={`${datepickerKey}-${period.id}`}
                      value={[period.startDate, period.endDate]}
                      disableTime={false}
                      leaveLimits={leaveLimits}
                      minDateTime={shiftMinDateTime}
                      maxDateTime={shiftMaxDateTime}
                      onChange={([start, end]) => {
                        if (!start || !end) return;
                        handlePeriodDateChange(period.id, start, end);
                      }}
                      dateTimeFormat={dateTimeFormat}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Bảng tính toán tạm thời - dùng chung cho mọi loại phép */}
        {leaveSummary && (
          <div className="pt-2">
            {(() => {
              const { hasUnpaid } = leaveSummary;
              const accentColor = hasUnpaid ? "#F97C00" : "#0057FF";
              const bgColor = hasUnpaid ? "#FEF2E6" : "#F0F9FF";

              const summaryColumns = [
                {
                  label: t("leave-request.total-leave-requested"),
                  value: leaveSummary.requestedLabel,
                  highlight: false,
                },
                {
                  label: t("leave-request.paid-with-leave-type", {
                    leaveType: selectedLeaveTypeName,
                  }),
                  value: leaveSummary.paidLabel,
                  highlight: false,
                },
                ...(leaveSummary.hasBridge
                  ? [
                    {
                      label: t("leave-request.paid-with-leave-type", {
                        leaveType: deductLeaveTypeName,
                      }),
                      value: leaveSummary.bridgeLabel,
                      highlight: false,
                    },
                  ]
                  : []),
                {
                  label: t("leave-request.unpaid-leave"),
                  value: leaveSummary.unpaidLabel,
                  highlight: true,
                },
              ];

              return (
                <div
                  className="rounded-md px-4 py-4 border-l-[6px]"
                  style={{
                    backgroundColor: bgColor,
                    borderLeftColor: accentColor,
                  }}
                >
                  <div className="w-full">
                    <div className="flex items-center gap-3 mb-3">
                      <ErrorIcon
                        sx={{ color: accentColor, fontSize: 25, flexShrink: 0 }}
                      />
                      <p className="text-sm font-semibold text-[#171717]">
                        {t("leave-request.exceeds-available-balance")}
                      </p>
                    </div>

                    <div className="flex-1 min-w-0 w-full">
                      <div className="flex items-center w-full flex-col sm:flex-row sm:divide-x sm:divide-[#E5E7EB]">
                        {summaryColumns.map((col, idx) => (
                          <div
                            key={col.label}
                            className={`flex-1 min-w-0 py-1 text-center ${idx > 0 ? "sm:pl-4" : ""
                              } ${idx < summaryColumns.length - 1 ? "sm:pr-4" : ""
                              }`}
                          >
                            <div className="flex items-center justify-center gap-1 mb-1">
                              <p
                                className="text-xs"
                                style={{
                                  color: col.highlight ? accentColor : "#737373",
                                }}
                              >
                                {col.label}
                              </p>
                              {col.showHelp && (
                                <DefaultTooltip
                                  title={t("leave-request.time-left")}
                                  readMore={false}
                                  content={
                                    <div className="flex flex-col gap-3">
                                      <p>
                                        {t("leave-request.time-left-tooltip-des", {
                                          hours: hoursOneDayLabel,
                                        })}
                                      </p>
                                    </div>
                                  }
                                >
                                  <QuestionIcon16pxSvg className="fill-[#BDBDBD] shrink-0 w-3.5 h-3.5" />
                                </DefaultTooltip>
                              )}
                            </div>
                            <p
                              className="text-sm font-semibold"
                              style={{
                                color: col.highlight ? accentColor : "#171717",
                              }}
                            >
                              {col.value}
                            </p>
                          </div>
                        ))}
                      </div>

                      {leaveSummary.footerType === "deduct" && leaveSummary.footerData && (
                        <p className="text-xs text-[#737373] mt-4">
                          {t("leave-request.balance-unpaid-summary", leaveSummary.footerData)}
                        </p>
                      )}

                      {leaveSummary.footerType === "bridge" && leaveSummary.footerData && (
                        <p className="text-xs text-[#737373] mt-4">
                          {t("leave-request.balance-bridge-unpaid-summary", {
                            balance: leaveSummary.footerData.balance,
                            bridge: leaveSummary.footerData.bridgeLabel,
                            deductLeaveType: leaveSummary.footerData.deductLeaveType,
                            unpaid: leaveSummary.footerData.unpaidLabel,
                          })}
                        </p>
                      )}

                      {leaveSummary.footerType === "non-deduct" && leaveSummary.footerData && (
                        <p className="text-xs text-[#737373] mt-4">
                          {t("leave-request.non-deduct-entitlement-exceeded-des", {
                            days: formatDecimalNumber(leaveSummary.footerData.maxDays),
                            total: formatDecimalNumber(leaveSummary.footerData.totalEntitlement),
                            used: formatDecimalNumber(leaveSummary.footerData.usedDays),
                            requested: leaveSummary.footerData.requestedLabel,
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        <div className="pt-5">
          <CustomInput
            required={false}
            title={t("common.leave-request")}
            inputType="text"
            name="requestReason"
            placeholder={t("common.leave-request")}
            handleInputChange={handleInputChange}
            value={formData.requestReason}
            className="w-full"
          />
          {/* <div
            className={`flex justify-end text-xs mt-1 ${
              formData.requestReason?.length >= MAX_REASON_LENGTH
                ? "text-red-500"
                : "text-gray-500"
            }`}
          >
            {formData.requestReason?.length || 0}/{MAX_REASON_LENGTH}
          </div> */}
        </div>

        {!isLeaveMode && isAdmin && leaveInfo.approvalStatus === "APPROVED" && (
          <div className="pt-4">
            <CustomInput
              required={false}
              title={t("common.approve-reject-reason")}
              inputType="text"
              name="approveReason"
              placeholder={t("common.approve-reject-reason")}
              handleInputChange={handleInputChange}
              value={formData.approveReason}
              className="w-full"
            />
            {/* <div
              className={`flex justify-end text-xs mt-1 ${
                formData.approveReason?.length >= MAX_REASON_LENGTH
                  ? "text-red-500"
                  : "text-gray-500"
              }`}
            >
              {formData.approveReason?.length || 0}/{MAX_REASON_LENGTH}
            </div> */}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton
            onClick={handleCloseDialog}
            disabled={isDialogLoading}
          >
            <p>{t("button.close")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            onClick={handleSubmit}
            disabled={isDialogLoading || !isInputValid}
          >
            <p>{t("button.update")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default EditLeaveDialog;
